import React, { useState, useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash, PiCamera, PiArrowUpRight } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import logger from "@/utils/logger";

const RESEARCH_IMAGES = [
  "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1576086213369-97a306d36557?w=600&h=400&fit=crop",
];

export default function UniversityResearch({ content, onContentChange, isSelected }: SectionContentProps) {
  const safeContent = content || {};
  const items = safeContent.items || [
    { title: "Are Social Networks Beneficial for our Society?", excerpt: "The American Journal of Applied Scientific Research (AJASR): A Rigorous Peer-Reviewed Platform.", link: "#" },
    { title: "Unveiling the Frontiers of Discovery and Innovation", excerpt: "The American Journal of Applied Scientific Research (AJASR): A Rigorous Peer-Reviewed Platform.", link: "#" },
    { title: "Exploring Environmental Frontiers: Unveiling Insights", excerpt: "Our commitment extends beyond the confines of the laboratory walls; we aim to bridge the gap.", link: "#" },
  ];

  const [activeUploadIdx, setActiveUploadIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addItem = () => {
    const newItems = [...items, { title: "New Research Item", excerpt: "Excerpt description of this research.", link: "#", image: "" }];
    onContentChange?.({ ...safeContent, items: newItems });
  };

  const removeItem = (idx: number) => {
    const newItems = items.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...safeContent, items: newItems });
  };

  const updateItem = (idx: number, field: string, value: string) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange?.({ ...safeContent, items: newItems });
  };

  const handleImageUpload = async (file: File, idx: number) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const uploadPromise = homepageApi.uploadImage(file);
    toast.promise(uploadPromise, {
      loading: "Uploading research image...",
      success: "Image uploaded successfully!",
      error: "Failed to upload image",
    });

    try {
      const response = await uploadPromise;
      if (response.success && response.data?.url) {
        updateItem(idx, "image", response.data.url);
      } else {
        toast.error(response.message || "Upload failed");
      }
    } catch (err) {
      logger.error(err);
      toast.error("Upload failed");
    }
  };

  return (
    <section className="py-24 px-6 lg:px-8 bg-white overflow-hidden">
      <div className="mx-auto max-w-7xl">
        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && activeUploadIdx !== null) {
              handleImageUpload(file, activeUploadIdx);
            }
          }}
        />

        {/* Section Top Header (Split Layout) */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-8 mb-16 border-b border-gray-100 pb-10">
          <div className="md:w-1/2">
            <h2 className="text-4xl lg:text-5xl font-bold font-serif text-[#1A1A1A] leading-tight tracking-wide uppercase">
              <EditableText
                value={safeContent.sectionTitle || "PUBLICATION & RESEARCH"}
                onChange={(val) => onContentChange?.({ ...safeContent, sectionTitle: val })}
                as="span"
                placeholder="Section Title"
              />
            </h2>
          </div>
          <div className="md:w-5/12 flex flex-col items-start gap-4">
            <p className="text-[#6c7279] text-sm leading-relaxed">
              <EditableText
                value={safeContent.description || "Dive into the vast array of courses offered at our university, spanning across 10+ specializations. Embrace an educational journey that ignites intellectual curiosity and nurtures intuitive learning among our students."}
                onChange={(val) => onContentChange?.({ ...safeContent, description: val })}
                as="span"
                placeholder="Description"
              />
            </p>
            <a
              href={safeContent.viewAllLink || "#"}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C2272D] hover:underline"
            >
              <EditableText
                value={safeContent.viewAllText || "Learn More"}
                onChange={(val) => onContentChange?.({ ...safeContent, viewAllText: val })}
                as="span"
                placeholder="Link Text"
              />
              <span className="font-sans">↗</span>
            </a>
          </div>
        </div>

        {/* 3-Column Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((item: any, idx: number) => {
            const itemImage = item.image || item.imageUrl || RESEARCH_IMAGES[idx % RESEARCH_IMAGES.length];
            return (
              <div
                key={idx}
                className="group relative flex flex-col gap-4 rounded-lg overflow-hidden transition-all duration-300"
              >
                {/* Image Wrap */}
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-50 rounded-lg">
                  <img
                    src={itemImage}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Overlay Canvas Buttons */}
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-20 flex gap-1.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveUploadIdx(idx);
                          fileInputRef.current?.click();
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-colors"
                        title="Upload Image"
                      >
                        <PiCamera className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {isSelected && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(idx);
                      }}
                      className="absolute top-2 right-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-md transition-colors"
                      title="Remove Item"
                    >
                      <PiTrash className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold font-serif text-[#1A1A1A] leading-tight mb-2 group-hover:text-[#C2272D] transition-colors">
                      <EditableText
                        value={item.title}
                        onChange={(val) => updateItem(idx, "title", val)}
                        as="span"
                        placeholder="Research Title"
                      />
                    </h3>
                    <p className="text-[#6c7279] text-xs leading-relaxed">
                      <EditableText
                        value={item.excerpt}
                        onChange={(val) => updateItem(idx, "excerpt", val)}
                        as="span"
                        placeholder="Excerpt description..."
                      />
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Builder Add Card */}
          {isSelected && (
            <button
              onClick={addItem}
              className="flex flex-col items-center justify-center aspect-[4/3] rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-gray-500 hover:bg-gray-100/50 hover:text-gray-600 transition-all"
            >
              <PiPlus className="h-8 w-8 mb-2" />
              <span className="text-sm font-semibold">Add Research Item</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

